// The one script URL the app ever registers as a service worker, minted through
// a Trusted Types policy where the browser enforces them (I-0a Batch 5 fix).
//
// Why: `public/_headers` sends `require-trusted-types-for 'script'`. In Chromium
// `navigator.serviceWorker.register()` is a TrustedScriptURL sink, so a plain
// string is rejected and no worker ever installs (no offline). The named policy
// `pebble-sw` (allowed by `trusted-types pebble-sw` in the CSP) accepts exactly
// `/sw.js` and nothing else. Browsers without Trusted Types (WebKit, Firefox)
// take the plain string. Local typing on purpose: no `@types/trusted-types`.

export const SW_URL = '/sw.js'
export const SW_TRUSTED_TYPES_POLICY = 'pebble-sw'

interface ScriptUrlPolicy {
  createScriptURL(input: string): unknown
}

interface TrustedTypesFactory {
  createPolicy(
    name: string,
    rules: { createScriptURL: (input: string) => string },
  ): ScriptUrlPolicy
}

// A policy name can be created only once per page under this CSP (no
// 'allow-duplicates'), so it is created lazily and kept.
let policy: ScriptUrlPolicy | undefined

export function swScriptUrl(): string {
  const trustedTypes = (globalThis as { trustedTypes?: TrustedTypesFactory }).trustedTypes
  if (!trustedTypes) {
    return SW_URL
  }
  policy ??= trustedTypes.createPolicy(SW_TRUSTED_TYPES_POLICY, {
    createScriptURL: (input) => {
      if (input !== SW_URL) {
        throw new TypeError(`${SW_TRUSTED_TYPES_POLICY}: script URL not allowed`)
      }
      return input
    },
  })
  // `register()` accepts a TrustedScriptURL at runtime; the DOM lib types it as
  // `string | URL`, hence the narrow cast here and nowhere else.
  return policy.createScriptURL(SW_URL) as string
}
